import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Zap, RefreshCw, Power, ShieldOff, Bell, BellOff, Play, X, ChevronDown, ChevronRight } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARKET_ORDER = ["NIFTY", "BANKNIFTY", "SENSEX"];

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

const toISTDateTime = (utcIso) => {
  if (!utcIso) return "—";
  try {
    let s = String(utcIso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      second: "2-digit", hour12: false,
    }).format(d).replace(",", "");
  } catch { return "—"; }
};

const PnlPill = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const pos = value >= 0;
  return (
    <span className="font-mono-data" style={{ color: pos ? "var(--bull)" : "var(--bear)" }}>
      {pos ? "+" : ""}{fmt(value)}%
    </span>
  );
};

const CloseReasonBadge = ({ reason }) => {
  if (!reason) return <span className="text-muted-3">—</span>;
  const map = {
    PROFIT_BOOKED_T2: { l: "T2", c: "var(--bull)" },
    TRAIL_PREMIUM:    { l: "TRAIL", c: "var(--bull)" },
    PREMIUM_SL:       { l: "PREM SL", c: "var(--bear)" },
    ATR_STOP:         { l: "ATR SL", c: "var(--bear)" },
    MR_FAILURE:       { l: "MR FAIL", c: "var(--bear)" },
    HARD_EXIT:        { l: "HARD", c: "#FFB66E" },
    MANUAL_EXIT:      { l: "MANUAL", c: "#8FB8FF" },
  };
  const cfg = map[reason] || { l: reason, c: "var(--text-muted)" };
  return (
    <span className="inline-block px-1.5 py-0.5 border font-mono-data text-[10px]"
          style={{ color: cfg.c, borderColor: cfg.c }}>
      {cfg.l}
    </span>
  );
};

const MarketCard = ({ marketKey, mkt, entries, cap, onToggle }) => (
  <div className="border border-grid p-3" data-testid={`setup-e-market-${marketKey.toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-mono-data uppercase tracking-widest text-primary">{marketKey}</span>
        <span
          className="text-[8px] px-1 py-0.5 border font-mono-data uppercase text-muted-3"
          style={{ borderColor: "var(--text-muted)" }}
          title={mkt.expiry_cadence === "monthly_last_dow"
            ? "Monthly contract — expires the last matching weekday of the month"
            : "Weekly contract"}
        >
          {mkt.expiry_cadence === "monthly_last_dow" ? "Monthly" : "Weekly"}
        </span>
      </div>
      <button
        onClick={() => onToggle(marketKey, !mkt.enabled)}
        data-testid={`setup-e-market-toggle-${marketKey.toLowerCase()}`}
        className="text-[9px] px-1.5 py-0.5 border font-mono-data uppercase"
        style={{
          color: mkt.enabled ? "var(--bull)" : "var(--text-muted)",
          borderColor: mkt.enabled ? "var(--bull)" : "var(--text-muted)",
        }}
      >
        {mkt.enabled ? "ON" : "OFF"}
      </button>
    </div>
    <div className="mt-1 grid grid-cols-2 gap-1 text-[10px] font-mono-data text-muted-2">
      <div>Expiry: <span style={{ color: mkt.is_expiry_today ? "var(--bull)" : "var(--text-muted)" }}>
        {mkt.is_expiry_today ? "TODAY" : mkt.next_expiry}
      </span></div>
      <div>Entries: <span style={{ color: entries >= cap ? "var(--bear)" : "var(--bull)" }}>{entries}</span>/{cap}</div>
      <div>Scan: {mkt.scan_window_ist}</div>
      <div>Hard exit: {mkt.hard_exit_ist}</div>
      <div>Score ≥ <span style={{ color: "#FFB66E" }}>{mkt.score_threshold}</span></div>
      <div>PE ≥ {mkt.premium_expansion_pct}%</div>
      <div>RVOL ≥ {mkt.rvol_threshold}×</div>
      <div>Vel ≥ {mkt.velocity_threshold}×</div>
    </div>
  </div>
);

const ScorePill = ({ score }) => {
  if (score == null) return <span className="text-muted-3">—</span>;
  let c = "var(--text-muted)";
  if (score >= 85) c = "var(--bull)";
  else if (score >= 70) c = "#FFB66E";
  else c = "var(--bear)";
  return <span className="font-mono-data" style={{ color: c }}>{score}/100</span>;
};

const ScoreBreakdownRow = ({ bd }) => {
  if (!bd) return null;
  return (
    <div className="text-[9px] font-mono-data text-muted-3 flex flex-wrap gap-x-2">
      <span>MR {bd.morning_breakout}</span>
      <span>PE {bd.premium_expansion}</span>
      <span>RVOL {bd.rvol}</span>
      <span>Vel {bd.velocity}</span>
      <span>Cdl {bd.candle}</span>
      <span>OI {bd.oi}</span>
      <span>ΔV {bd.delta_volume}</span>
    </div>
  );
};

const DetailStat = ({ label, value, color }) => (
  <span>
    {label}{" "}
    <span className="font-semibold" style={color ? { color } : undefined}>
      {value}
    </span>
  </span>
);

// 2026-08-18 — Expandable closed-history row. Surfaces everything already
// stored on the trade doc (score breakdown, phase3 confirmations, MFE/MAE,
// entry-time context, and — importantly — live_trade/live_error/order_status
// so a "LIVE was ON but this landed as PAPER" case is diagnosable from the
// UI instead of needing a code read. All fields were already reaching the
// frontend via _serialize()'s unfiltered pass-through; this was purely a
// rendering gap.
const SetupEHistoryRow = ({ t, i }) => {
  const [expanded, setExpanded] = useState(false);
  const bd = t.score_breakdown;
  const confirmations = t.phase3?.confirmations;
  return (
    <>
      <tr
        data-testid={`setup-e-history-row-${i}`}
        className="border-b border-grid hover:bg-white/[0.02] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-3 py-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="text-muted-3">
              {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </span>
            {toISTDateTime(t.entered_at)}
          </span>
        </td>
        <td className="px-3 py-2">{t.market}</td>
        <td className="px-3 py-2">
          <span style={{ color: t.direction === "BULL" ? "var(--bull)" : "var(--bear)" }}>
            {t.direction}
          </span>
        </td>
        <td className="px-3 py-2 text-right">{t.strike} {t.option_side}</td>
        <td className="px-3 py-2 text-right">₹{fmt(t.entry_premium)}</td>
        <td className="px-3 py-2 text-right">{t.close_premium ? `₹${fmt(t.close_premium)}` : "—"}</td>
        <td className="px-3 py-2 text-right">
          <PnlPill value={t.final_pnl_pct} />
          {t.quick_leg_enabled && t.quick_leg_closed && (
            <div className="text-[9px] text-muted-3 mt-0.5">
              blended · runner {fmt(t.runner_pnl_pct ?? t.final_pnl_pct)}%
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-center">
          {!t.quick_leg_enabled ? (
            <span className="text-muted-3">—</span>
          ) : t.quick_leg_closed ? (
            <span style={{ color: "var(--bull)" }} title={`Closed ${t.quick_leg_qty ?? ""} qty @ ₹${fmt(t.quick_leg_exit_premium)}`}>
              ✓ +{fmt(t.quick_leg_pnl_pct)}%
            </span>
          ) : (
            <span className="text-muted-3">not hit</span>
          )}
        </td>
        <td className="px-3 py-2 text-center"><ScorePill score={t.score} /></td>
        <td className="px-3 py-2 text-center"><CloseReasonBadge reason={t.close_reason} /></td>
        <td className="px-3 py-2 text-right text-muted-2">{t.holding_time_min ? `${fmt(t.holding_time_min, 0)}m` : "—"}</td>
      </tr>
      {expanded && (
        <tr className="border-b border-grid" style={{ background: "rgba(255,255,255,0.015)" }}>
          <td colSpan={11} className="px-3 py-2.5">
            <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono-data text-[11px] text-muted-2 pl-6">
              <DetailStat
                label="Mode"
                value={t.live_trade ? "LIVE" : "PAPER"}
                color={t.live_trade ? "var(--bull)" : "var(--text-muted)"}
              />
              {!t.live_trade && t.live_error && (
                <DetailStat label="Live BUY failed" value={String(t.live_error)} color="var(--bear)" />
              )}
              <DetailStat label="Order status" value={t.order_status || "—"} />
              <DetailStat label="Entry spot" value={t.entry_spot == null ? "—" : fmt(t.entry_spot)} />
              <DetailStat label="Morning range" value={t.morning_range_low == null ? "—" : `${fmt(t.morning_range_low)} – ${fmt(t.morning_range_high)}`} />
              <DetailStat label="ATR stop (spot)" value={t.atr_stop_spot == null ? "—" : fmt(t.atr_stop_spot)} />
              <DetailStat label="Premium SL" value={t.premium_sl == null ? "—" : `₹${fmt(t.premium_sl)}`} />
              <DetailStat label="T1 / T2" value={t.t1_premium == null ? "—" : `₹${fmt(t.t1_premium)} / ₹${fmt(t.t2_premium)}`} />
              <DetailStat label="MFE / MAE" value={`${t.mfe_pct != null ? fmt(t.mfe_pct) : "—"}% / ${t.mae_pct != null ? fmt(t.mae_pct) : "—"}%`}
                          color={t.mfe_pct != null && t.mfe_pct > 0 ? "var(--bull)" : undefined} />
              <DetailStat label="Velocity" value={t.velocity_ratio == null ? "—" : `${fmt(t.velocity_ratio)}×`} />
              <DetailStat label="Premium exp" value={t.premium_expansion_pct == null ? "—" : `${fmt(t.premium_expansion_pct)}%`} />
              <DetailStat label="RVOL" value={t.rvol == null ? "—" : `${fmt(t.rvol)}×`} />
              <DetailStat label="OI reason" value={t.oi_reason || "—"} />
              <DetailStat label="Phase 3 confirmations" value={confirmations == null ? "—" : `${confirmations}/5`} />
            </div>
            {bd && (
              <div className="pl-6 mt-1.5">
                <span className="text-[9px] uppercase tracking-wider text-muted-3 mr-2">Score breakdown</span>
                <ScoreBreakdownRow bd={bd} />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export const SetupEMomentumEngine = () => {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [collapsed, toggleCollapsed] = useCollapsed("setup-e-v2-collapsed", false);

  const load = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([
        axios.get(`${API}/setup-e/today`),
        axios.get(`${API}/setup-e/history?days=7`),
      ]);
      setData(t.data);
      setHistory(h.data);
    } catch (e) {
      console.error("setup-e-v2 load failed", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/setup-e/scan`);
      const n = Object.keys(r.data?.markets || {}).length;
      toast.success(`Scan complete · ${n} markets`);
      await load();
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const pollSnapshot = async (market) => {
    try {
      const r = await axios.post(`${API}/setup-e/snapshot`, { market });
      toast.success(`${market} snapshot @ ATM ${r.data.atm_strike}`);
      await load();
    } catch { toast.error(`${market} snapshot failed`); }
  };

  const toggleLive = async () => {
    if (!data) return;
    const next = !data.live_trading_enabled;
    if (next && !window.confirm("Enable LIVE trading for Setup E V2? Real Upstox BUY orders will fire on next qualifying signal across all enabled markets.")) return;
    try {
      await axios.post(`${API}/setup-e/live-status`, { enabled: next });
      toast.success(`Live trading ${next ? "ON" : "OFF"}`);
      await load();
    } catch { toast.error("Failed to toggle live"); }
  };

  const toggleAlerts = async () => {
    if (!data) return;
    try {
      await axios.post(`${API}/setup-e/alerts`, { enabled: !data.alerts_enabled });
      await load();
    } catch { toast.error("Failed to toggle alerts"); }
  };

  const toggleMarket = async (market, enabled) => {
    try {
      await axios.post(`${API}/setup-e/market-enabled`, { market, enabled });
      toast.success(`${market} ${enabled ? "enabled" : "disabled"}`);
      await load();
    } catch { toast.error(`Failed to toggle ${market}`); }
  };

  const manualExit = async (trade_id) => {
    if (!window.confirm("Exit this Setup E V2 trade now at current LTP?")) return;
    try {
      const r = await axios.post(`${API}/setup-e/manual-exit`, { trade_id });
      const pnl = r.data.final_pnl_pct ?? 0;
      toast.success(`Exited @ ${pnl >= 0 ? "+" : ""}${Number(pnl).toFixed(2)}%`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Exit failed");
    }
  };

  const closedTrades = useMemo(
    () => (history?.trades || []).filter((t) => t.status === "CLOSED"),
    [history]
  );

  if (loading) return <Skeleton className="h-40" />;
  if (!data) return null;

  const totalPnl = history?.summary?.gross_pnl_pct ?? 0;
  const anyExpiryToday = Object.values(data.markets || {}).some((m) => m.is_expiry_today);

  return (
    <section className="border border-grid" data-testid="setup-e-section">
      <header className="border-b border-grid px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} testId="setup-e-collapse" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
              09 / SETUP E V2 · UNIFIED EXPIRY MOMENTUM
            </div>
            <h3 className="font-heading text-lg mt-1 flex items-center gap-2">
              <Zap size={16} style={{ color: "#FFB66E" }} />
              Expiry Momentum Engine · NIFTY · BANKNIFTY · SENSEX
            </h3>
            <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="setup-e-description">
              <strong style={{ color: data.live_trading_enabled ? "var(--bull)" : "var(--text-primary)" }}>
                {data.live_trading_enabled ? "LIVE" : "PAPER"}
              </strong>{" · "}
              Weighted-scoring engine (max <strong>100 pts</strong>) across NIFTY &amp; SENSEX weekly
              expiries and BANKNIFTY's monthly expiry (last Tuesday of the month).
              Scores <strong>Morning-range breakout (30)</strong> · <strong>Premium expansion (20)</strong> ·
              RVOL (10) · Velocity (10) · Candle (10) · OI (10) · Delta-volume (10). Requires
              <strong>{" ≥ 2 of 5 momentum confirmations"}</strong> {"(premium ↑, volume ↑, spot > VWAP, 5EMA > 20EMA, OI)"}. Entry when score ≥ market threshold; picks ATM or 1-OTM by
              liquidity. Buys <strong>{data.lots_per_trade ?? 2} lots</strong> per entry (still ONE
              entry against the daily cap) — <strong>1 lot ("quick leg")</strong> books in full at
              +<strong>{data.quick_leg_target_pct ?? 15}%</strong>, the rest ("runner") rides the
              full engine: T1 30% (activates 15% trail), T2 50% full exit, hard exit 14:45 IST.
              Max 2 entries per market per day.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleLive}
            data-testid="setup-e-live-toggle-btn"
            // 2026-09-12 — see CandleBreakout.jsx's live-toggle comment;
            // same "apply everywhere" fix (filled bg on ON, not just color).
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-data text-xs uppercase"
            style={{
              color: data.live_trading_enabled ? "var(--bull)" : "var(--bear)",
              borderColor: data.live_trading_enabled ? "var(--bull)" : "var(--bear)",
              background: data.live_trading_enabled ? "var(--bull-muted)" : "transparent",
            }}
          >
            {data.live_trading_enabled ? <Power size={12} /> : <ShieldOff size={12} />}
            Live · {data.live_trading_enabled ? "ON" : "OFF"}
          </button>

          <button
            onClick={toggleAlerts}
            data-testid="setup-e-alerts-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-data text-xs uppercase"
            style={{
              color: data.alerts_enabled ? "var(--bull)" : "var(--text-muted)",
              borderColor: data.alerts_enabled ? "var(--bull)" : "var(--text-muted)",
            }}
          >
            {data.alerts_enabled ? <Bell size={12} /> : <BellOff size={12} />}
            {data.alerts_enabled ? "ALERTING" : "PAUSED"}
          </button>

          <button
            onClick={runScan}
            disabled={scanning}
            data-testid="setup-e-scan-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid text-xs uppercase font-mono-data hover:bg-white/[0.03]"
          >
            {scanning ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
            Run Scan (All)
          </button>
        </div>
      </header>

      {!collapsed && (
        <div className="p-5 space-y-5">
          {/* Market cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {MARKET_ORDER.filter((k) => data.markets?.[k]).map((k) => (
              <div key={k} className="relative">
                <MarketCard
                  marketKey={k}
                  mkt={data.markets[k]}
                  entries={data.entries_today_by_market?.[k] ?? 0}
                  cap={data.max_entries_per_day_per_market ?? 2}
                  onToggle={toggleMarket}
                />
                <button
                  onClick={() => pollSnapshot(k)}
                  data-testid={`setup-e-snapshot-btn-${k.toLowerCase()}`}
                  className="absolute bottom-2 right-2 text-[9px] px-1.5 py-0.5 border border-grid font-mono-data uppercase hover:bg-white/[0.03] flex items-center gap-1"
                >
                  <Activity size={9} /> Snap
                </button>
              </div>
            ))}
          </div>

          {/* Weekly P&L strip */}
          <div className="border border-grid p-3 font-mono-data text-xs flex flex-wrap items-center gap-x-6 gap-y-1">
            <span className="text-muted-3 uppercase text-[9px] tracking-widest">7-day rollup</span>
            <span>Trades: {history?.summary?.total ?? 0}</span>
            <span>Win rate: <span style={{ color: (history?.summary?.win_rate_pct ?? 0) >= 50 ? "var(--bull)" : "var(--bear)" }}>
              {history?.summary?.win_rate_pct ?? 0}%
            </span></span>
            <span>Wins/Losses: {history?.summary?.wins ?? 0}/{history?.summary?.losses ?? 0}</span>
            <span>Gross P&L: <span style={{ color: totalPnl >= 0 ? "var(--bull)" : "var(--bear)" }}>
              {totalPnl >= 0 ? "+" : ""}{fmt(totalPnl)}%
            </span></span>
            {!anyExpiryToday && (
              <span className="text-muted-3 ml-auto">No expiry today across NIFTY / BANKNIFTY / SENSEX</span>
            )}
          </div>

          {/* Open trades */}
          {data.open_trades?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-2 font-mono-data mb-2">Open Positions</div>
              <div className="border border-grid overflow-x-auto">
                <table className="w-full text-xs font-mono-data">
                  <thead>
                    <tr className="border-b border-grid text-muted-3 uppercase text-[9px] tracking-widest">
                      <th className="px-3 py-2 text-left">Entry IST</th>
                      <th className="px-3 py-2 text-left">Market</th>
                      <th className="px-3 py-2 text-left">Dir</th>
                      <th className="px-3 py-2 text-right">Strike</th>
                      <th className="px-3 py-2 text-right">Entry ₹</th>
                      <th className="px-3 py-2 text-right">Current ₹</th>
                      <th className="px-3 py-2 text-right">P&L</th>
                      <th className="px-3 py-2 text-center">Score</th>
                      <th className="px-3 py-2 text-center">T1</th>
                      <th className="px-3 py-2 text-center">Quick Leg</th>
                      <th className="px-3 py-2 text-center">Type</th>
                      <th className="px-3 py-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.open_trades.map((t, i) => (
                      <tr key={t.id} className="border-b border-grid" data-testid={`setup-e-open-row-${i}`}>
                        <td className="px-3 py-2">{toISTDateTime(t.entered_at)}</td>
                        <td className="px-3 py-2">{t.market}</td>
                        <td className="px-3 py-2">
                          <span style={{ color: t.direction === "BULL" ? "var(--bull)" : "var(--bear)" }}>
                            {t.direction}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">{t.strike} {t.option_side}</td>
                        <td className="px-3 py-2 text-right">₹{fmt(t.entry_premium)}</td>
                        <td className="px-3 py-2 text-right">{t.current_premium ? `₹${fmt(t.current_premium)}` : "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <PnlPill value={t.pnl_pct} />
                          {t.quick_leg_enabled && t.quick_leg_closed && (
                            <div className="text-[9px] text-muted-3 mt-0.5" title="Blended: quick leg (locked) + runner leg (live)">
                              blended · runner {t.runner_pnl_pct != null ? `${t.runner_pnl_pct >= 0 ? "+" : ""}${fmt(t.runner_pnl_pct)}%` : "—"}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center"><ScorePill score={t.score} /></td>
                        <td className="px-3 py-2 text-center">{t.t1_booked ? "✓" : "—"}</td>
                        <td className="px-3 py-2 text-center">
                          {!t.quick_leg_enabled ? (
                            <span className="text-muted-3">—</span>
                          ) : t.quick_leg_closed ? (
                            <span style={{ color: "var(--bull)" }} title={`Booked ${t.quick_leg_qty} qty @ ₹${fmt(t.quick_leg_exit_premium)} (${t.quick_leg_pnl_pct >= 0 ? "+" : ""}${fmt(t.quick_leg_pnl_pct)}%)`}>
                              ✓ {t.quick_leg_pnl_pct >= 0 ? "+" : ""}{fmt(t.quick_leg_pnl_pct)}%
                            </span>
                          ) : (
                            <span className="text-muted-3" title={`Books 1 lot (${t.quick_leg_qty} qty) in full at +${t.quick_leg_target_pct}%`}>
                              pending @ {t.quick_leg_target_pct}%
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="text-[10px] px-1.5 py-0.5 border font-mono-data"
                                style={{ color: t.live_trade ? "var(--bull)" : "var(--text-muted)",
                                         borderColor: t.live_trade ? "var(--bull)" : "var(--text-muted)" }}>
                            {t.live_trade ? "LIVE" : "PAPER"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => manualExit(t.id)}
                            data-testid={`setup-e-exit-btn-${i}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] hover:bg-white/[0.03]"
                            style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
                          >
                            <X size={9} /> Exit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 7-day history */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-2 font-mono-data mb-2">
              Trade History · last 7 days · {closedTrades.length} closed
            </div>
            {closedTrades.length === 0 ? (
              <div className="border border-grid p-6 text-center text-muted-3 text-xs font-mono-data">
                No closed trades yet. Engine fires on NIFTY Tuesdays, SENSEX Thursdays, and
                BANKNIFTY's last Tuesday of the month.
              </div>
            ) : (
              <div className="border border-grid overflow-x-auto">
                <table className="w-full text-xs font-mono-data">
                  <thead>
                    <tr className="border-b border-grid text-muted-3 uppercase text-[9px] tracking-widest">
                      <th className="px-3 py-2 text-left">Entry IST</th>
                      <th className="px-3 py-2 text-left">Market</th>
                      <th className="px-3 py-2 text-left">Dir</th>
                      <th className="px-3 py-2 text-right">Strike</th>
                      <th className="px-3 py-2 text-right">Entry ₹</th>
                      <th className="px-3 py-2 text-right">Exit ₹</th>
                      <th className="px-3 py-2 text-right">P&L</th>
                      <th className="px-3 py-2 text-center">Quick Leg</th>
                      <th className="px-3 py-2 text-center">Score</th>
                      <th className="px-3 py-2 text-center">Reason</th>
                      <th className="px-3 py-2 text-right">Hold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.map((t, i) => (
                      <SetupEHistoryRow key={t.id} t={t} i={i} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {history?.per_market && Object.keys(history.per_market).length > 0 && (
            <div className="border border-grid p-3 font-mono-data text-[10px]">
              <div className="text-muted-3 uppercase text-[9px] tracking-widest mb-1">Per-market rollup</div>
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {Object.entries(history.per_market).map(([mk, v]) => (
                  <span key={mk} data-testid={`setup-e-per-market-${mk.toLowerCase()}`}>
                    <span className="text-primary">{mk}</span>: {v.trades}T · {v.win_rate_pct}%WR ·
                    <span style={{ color: v.gross_pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                      {" "}{v.gross_pnl_pct >= 0 ? "+" : ""}{v.gross_pnl_pct}%
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] text-muted-3 font-mono-data">
            Historical backtest lives at <a href="/backtests" className="underline hover:text-primary">/backtests</a> —
            select the Setup E V2 tab to sweep filters across all three markets.
          </div>
        </div>
      )}
    </section>
  );
};

// Backwards-compatible export used by Dashboard.jsx
export const SetupEGammaSpike = SetupEMomentumEngine;